import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import MagneticButton from "@/components/MagneticButton";
import WJCore from "@/components/WJCore";
import WJEnvironment from "@/components/WJEnvironment";
import WJAnnotations from "@/components/WJAnnotations";
import { HERO } from "@/constants/testIds";
import { EASE } from "@/lib/motion";
import { useT } from "@/i18n/LanguageProvider";

export default function Hero() {
  const { t, lang } = useT();

  const scrollToWork = () =>
    document.getElementById("work")?.scrollIntoView({ behavior: "smooth" });
  const scrollToContact = () =>
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="home"
      data-testid={HERO.root}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505] noise"
    >
      {/* Hero-wide environment: WJ orbital / aura / light-field extended across the entire canvas */}
      <WJEnvironment />
      {/* Right-side "system detail" annotations linked to the WJ Core */}
      <WJAnnotations />

      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#050505] to-transparent z-10" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#050505] to-transparent z-10" />

      {/* Scroll-linked fade/drift is pure CSS (hero-drift class) — zero JS on scroll */}
      <div className="hero-drift relative z-20 max-w-7xl mx-auto px-6 md:px-10 pt-36 md:pt-40 pb-24 min-h-[100svh] flex flex-col justify-center">
        {/* Copy — flows left, occupies the left ~55% of the hero */}
        <div className="w-full md:max-w-[62%] lg:max-w-[58%] flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
            className="hidden md:flex items-center gap-4 mb-8 text-[10px] tracking-[0.34em] uppercase text-white/35 font-body"
          >
            <span className="h-px w-10 bg-white/25" />
            <span>WJ · Studio</span>
            <span className="w-1 h-1 rounded-full bg-[#28B37A]/70" />
            <span>Malaysia</span>
          </motion.div>

          <motion.div
            key={`pill-${lang}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.35 }}
            className="inline-flex items-center gap-2 self-start rounded-full glass px-4 py-2 text-xs font-body tracking-wide text-white/70"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-white/70" />
            {t("hero.pill")}
          </motion.div>

          <motion.h1
            key={`h1-${lang}`}
            data-testid={HERO.headline}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.5 }}
            className="font-display mt-8 max-w-4xl text-balance text-5xl sm:text-6xl md:text-7xl lg:text-[76px] xl:text-8xl font-semibold leading-[1.02] tracking-tight text-white"
          >
            {t("hero.headline_a")}{" "}
            <span className="text-gradient-accent">{t("hero.headline_b")}</span>{" "}
            {t("hero.headline_c")}
          </motion.h1>

          <motion.p
            key={`sub-${lang}`}
            data-testid={HERO.sub}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.7 }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-white/60 font-body"
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.9 }}
            className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <MagneticButton
              data-testid={HERO.ctaPrimary}
              onClick={scrollToContact}
              className="group inline-flex items-center gap-2 rounded-full bg-white text-black px-7 py-3.5 text-sm font-medium hover:bg-white/90 shadow-[0_10px_40px_-15px_rgba(255,255,255,0.35)]"
            >
              {t("hero.cta_primary")}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </MagneticButton>
            <MagneticButton
              data-testid={HERO.ctaSecondary}
              onClick={scrollToWork}
              className="group inline-flex items-center gap-2 rounded-full glass text-white px-7 py-3.5 text-sm font-medium hover:border-white/25"
            >
              {t("hero.cta_secondary")}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </MagneticButton>
          </motion.div>
        </div>

        {/* WJ Core focal source — positioned at ~62% x, mid-height on desktop.
            On mobile it stacks below the copy. The orbital field around it
            (WJEnvironment) extends across the whole hero. */}
        <div
          data-testid={HERO.canvas}
          className="wj-core-anchor mt-16 md:mt-0 md:absolute md:top-1/2 md:left-[62%] md:-translate-x-1/2 md:-translate-y-1/2 pointer-events-none flex items-center justify-center"
        >
          <WJCore />
        </div>

        <motion.div
          data-testid={HERO.scrollHint}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="hidden md:flex absolute bottom-10 left-6 md:left-10 items-center gap-3 text-xs tracking-widest uppercase text-white/40 font-body"
        >
          <span className="h-px w-10 bg-white/20" />
          {t("hero.scroll")}
        </motion.div>

        {/* System status line — very restrained, sits at the bottom-right of the hero */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="hidden xl:flex absolute bottom-10 right-8 2xl:right-14 items-center gap-3 text-[10px] tracking-[0.28em] uppercase text-white/35 font-body"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#28B37A] animate-pulse" />
          <span>3.1390°N · 101.6869°E</span>
          <span className="h-px w-8 bg-white/20" />
          <span className="text-[#EFE4CE]/70">Studio Online</span>
        </motion.div>
      </div>
    </section>
  );
}
